import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 2.3 visual preview at /preview/lesson/2.3
 *
 * "When to trust, when to verify, when to walk away" — Week 2, lesson 3.
 *
 * Builds on 2.1 (right tool) and 2.2 (right prompt). Once you have an answer,
 * what do you actually do with it? Teaches the trust ladder: three zones
 * (Trust, Verify, Walk away) plus four concrete verification methods.
 *
 * Promotes "Hallucination" as the new vocab term with the full 4-block
 * treatment. 3 zone-sorting drills where students decide what to do with
 * a given AI output.
 */

export default function LessonPreviewPage2_3() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-turn',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 2 · Working with AI · Lesson 2.3
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        When to trust, when to verify, when to walk away
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Judgment · Character</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">3 trust ladder drills</span>
      </div>
    </header>
  );
}

// ---------- Cold open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="Three answers. All confident. Three very different next moves."
      accent="amber"
    >
      <p>
        Same student. Same Saturday morning. They use AI for three different
        things in the same hour. The AI gives a confident answer every time.
        Here is what trust, verify, and walk away actually look like in real
        life.
      </p>

      <ZoneScenarios />

      <p className="mt-4">
        Three confident answers. Three completely different next moves.
        Treating all three the same way is what burns time and breaks trust.
        Treating them the same way is also the default behavior for most
        people who never learned the difference.
      </p>
      <p className="text-text">
        2.1 was the tool. 2.2 was the prompt. 2.3 is what you do with the
        answer. Trust ladder, every time.
      </p>
    </Section>
  );
}

// ---------- From last time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time · Lesson 2.2"
      eyebrow="Three questions on the five-part prompt. No looking back."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> Name the five
          parts of a good prompt, in order.
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> Which three parts
          do most weak prompts skip?
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> Your AI response
          is close but the tone is wrong. What is your move?
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        2.3 assumes you picked the right tool and wrote a strong prompt. If
        either of those is shaky, the trust ladder is still useful but the
        answers are coming back weaker than they need to be.
      </p>
    </Section>
  );
}

// ---------- Teaching ----------

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      label="Teaching"
      eyebrow="Three zones. Four verification methods. One rule about when to walk away."
    >
      <p>
        Every AI answer lands in one of three zones. Recognizing the zone
        before you act on the answer is the move. Treating every answer the
        same way burns time on low-stakes work and ships errors on
        high-stakes work. Both directions are expensive.
      </p>

      <Subhead n="1" title="The three zones" />
      <TrustLadder />

      <p>
        The zone is decided by two things, in order: the stakes if the answer
        is wrong, and whether you have a fast way to check. High stakes plus
        no easy check pushes the answer up the ladder toward verify or walk
        away. Low stakes plus easy reversibility keeps it at trust.
      </p>

      <Subhead n="2" title="Why models confidently make things up" />
      <p>
        Before we talk about how to verify, you need a vocabulary word for
        what you are verifying against. The thing models do that the four
        checks from 1.4 are designed to catch has a name. It is the single
        most important word in this entire week.
      </p>

      <VocabCard
        term="Hallucination"
        definition="When a model generates content that sounds confident and plausible but is wrong: a fake citation, an invented quote, a person who never existed, a date that is off, a fact that was never true. The model is not lying because lying requires knowing the truth. The model is pattern-matching to what an answer looks like, not to what is true."
        analogy="A student in a class they barely studied for. The test asks a question they do not know. They write something that sounds like a real answer because they have read enough real answers to fake the shape. The shape is right. The substance is invented."
        hook="A confident-sounding wrong answer. The pattern of an answer, without the truth of one."
        matters="Hallucination is the default failure mode of AI, not a bug being fixed. Frontier models still hallucinate on real tasks at meaningful rates. Most people who get burned by AI are not getting burned by lies. They are getting burned by confident, well-written nonsense they did not verify. The trust ladder exists because hallucination exists."
      />

      <Subhead n="3" title="Four ways to actually verify" />
      <p>
        Verify does not mean read it over carefully and trust your instincts.
        Verify means do a specific thing that catches the specific failure
        mode. Four methods. Pick the one that fits the answer.
      </p>

      <VerifyMethods />

      <p>
        The verify zone is where most of the work happens, and where most
        students skip. Pick one method, use it, move on. A 30-second
        cross-check beats a five-minute reread every time, because reading
        the same answer twice rarely catches what you missed the first time.
      </p>

      <Subhead n="4" title="When to walk away" />
      <p>
        Walking away is not failure. It is judgment. There are three signals
        that an AI conversation has crossed into walk-away territory.
      </p>

      <WalkAwaySignals />

      <p>
        Walking away does not mean giving up on the question. It means using a
        different tool: a person, a primary source, your own head, or
        sometimes silence. The AI is a teammate, not a replacement. Some
        questions are not its to answer.
      </p>
    </Section>
  );
}

// ---------- Your turn ----------

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      label="Your turn"
      eyebrow="Three real AI outputs. For each one, pick the zone."
    >
      <p>
        For each scenario you see what the AI gave you. Pick Trust, Verify,
        or Walk away. Then reveal the model verdict and check your reasoning
        against the ladder.
      </p>

      <ZoneDrill
        n={1}
        scenario="You asked AI to suggest five birthday gift ideas for your younger sister who likes art and astronomy."
        output="The AI lists five gifts: a watercolor set, a beginner's telescope, a star map poster, an art history book, and a sketchbook with constellation prompts. All five are reasonable. None require checking anything."
        correct="trust"
        verdict="Trust. Low stakes, brainstorm-quality task, no facts to verify, no claims under your name, no consequence if one suggestion is bad. You pick the one you like. This is exactly what AI is good at and exactly what the trust zone is for. Reading the list carefully for 30 seconds is wasted time."
      />

      <ZoneDrill
        n={2}
        scenario="You asked AI for the three biggest causes of the Civil War for a US History essay. It gives you a clean three-paragraph response with specific dates and one quote from Abraham Lincoln."
        output="The response reads well: states' rights, slavery and economic differences, with a Lincoln quote about 'a house divided against itself cannot stand' dated to 1858. Cohesive. Confident. About to go into your essay."
        correct="verify"
        verdict="Verify. This is going out under your name in an academic context. The quote and the date are exactly the kind of detail that hallucinations love. Two quick checks: search the Lincoln quote (real, it does come from 1858), and cross-check the three causes against your textbook or one other source. Thirty seconds of verification protects the grade and your reputation as a student who knows what they are talking about."
      />

      <ZoneDrill
        n={3}
        scenario="You are wrestling with whether to tell your best friend that another friend has been spreading rumors about them behind their back. You ask AI for advice."
        output="The AI gives you a balanced response: arguments for telling, arguments against, suggestions for how to bring it up if you decide to. It sounds thoughtful."
        correct="walk-away"
        verdict="Walk away. This is not the AI's job. The model does not know your friend, does not know the history, does not know what your friendship can hold, and cannot read either of their faces. It is generating a competent-looking answer to a question that only you and the people in your life can really answer. Use the response as something to think against if you want, but the decision belongs to a human conversation, with another human you trust. Some questions are not for the AI."
      />
    </Section>
  );
}

// ---------- Quick check ----------

function QuickCheck() {
  const items = [
    {
      q: 'Name the three zones of the trust ladder, top to bottom.',
      a: 'Trust, Verify, Walk away. Trust is low-stakes, easily reversible work where the model is on-task. Verify is anything that goes out under your name or anything with facts, numbers, dates, names, citations, or claims. Walk away is judgment, ethics, things only you can know, or any chat where the model keeps missing after three follow-ups.',
    },
    {
      q: 'What is a hallucination, in one sentence?',
      a: 'A confident-sounding piece of AI output that is wrong: an invented quote, a fake citation, a date that is off, a fact that was never true. The model is not lying. It is generating what an answer should look like rather than what is true.',
    },
    {
      q: 'Name the four verification methods and one example of when to use each.',
      a: 'Cross-check (open a second source) for facts and numbers. Reverse the prompt (ask the AI to argue the opposite) for claims and conclusions. Run it (execute the code, do the math, try the recipe) for anything you can actually test. Ask a human for judgment, ethics, or "is this weird?" calls.',
    },
    {
      q: 'You ran a math word problem through AI. The answer looks right. What is the fastest verify move?',
      a: 'Run it. Plug the numbers into a calculator yourself, or ask the AI to re-do it with code execution on. Math is one of the easiest things to verify because it is binary: the answer is right or it is wrong. Rereading the explanation is slow and rarely catches arithmetic errors. Just run it.',
    },
    {
      q: 'A friend says "AI gave me really good advice about my breakup." Where is the trust ladder warning sign?',
      a: 'Walk-away territory. The AI does not know either of them, did not see the relationship, and cannot tell when an answer sounds wise versus when it actually is. It is generating a competent-looking answer to a question only people in the situation can really answer. Use it as something to think against if helpful. The decision belongs to a human conversation.',
    },
  ];

  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Five questions. Answer first. Then reveal."
    >
      <div className="space-y-3">
        {items.map((it, i) => (
          <QuickCheckItem key={i} n={i + 1} q={it.q} a={it.a} />
        ))}
      </div>
    </Section>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

// ---------- Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Run it on every AI answer that matters."
    >
      <div className="rounded-xl border border-amber/40 bg-gradient-to-br from-amber/[0.05] to-amber/[0.02] p-8" data-lockcard-body>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Lesson 2.3 · Lock card · Trust ladder
          </p>
          <button className="text-xs text-muted hover:text-text">
            ⤓ Save image
          </button>
        </div>

        <p className="mt-4 text-lg leading-relaxed text-text">
          Every answer lands in one zone. Three zones. Pick before you act.
        </p>

        <div className="mt-6 space-y-3">
          <ZoneTile
            zone="trust"
            label="Trust"
            q="Use it. Move on."
            sub="Low stakes, easily reversible, no facts under your name. Brainstorming, tone fixes, casual help. Reading carefully here is wasted time."
          />
          <ZoneTile
            zone="verify"
            label="Verify"
            q="Use it, but check the bones first."
            sub="Goes out under your name. Has facts, numbers, dates, names, citations, code that runs, or claims a reader will believe. Pick one of four methods: cross-check, reverse the prompt, run it, ask a human."
          />
          <ZoneTile
            zone="walk-away"
            label="Walk away"
            q="Close the chat. Use a different tool."
            sub="Judgment, ethics, things only you can know, or chats where the model keeps missing after three follow-ups. Walking away is not failure. It is recognizing the question is not the AI's to answer."
          />
        </div>

        <div className="mt-6 rounded-lg border border-line bg-ink/60 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The principle behind the ladder
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text/90">
            Hallucination is the default failure mode of AI, not a bug being
            fixed. The trust ladder is how you stay safe in a tool that will
            sometimes confidently make things up. Pick the zone before you
            act. Verify when it matters. Walk away when it is not the AI's
            question.
          </p>
        </div>

        <p className="mt-6 text-sm italic text-muted">
          Tool. Prompt. Answer. Zone. Verify or walk. Every time.
        </p>
      </div>
    </Section>
  );
}

// ---------- Custom components for 2.3 ----------

function ZoneScenarios() {
  const scenarios: Array<{
    zone: 'trust' | 'verify' | 'walk-away';
    label: string;
    ask: string;
    ai: string;
    move: string;
  }> = [
    {
      zone: 'trust',
      label: 'Trust',
      ask: 'Brainstorm five rainy-day activities for an 8-year-old who likes legos.',
      ai: 'Lego challenge cards, indoor obstacle course with lego rules, build-and-photo scavenger hunt, design a lego game then play it, build the family pet in lego.',
      move: 'Pick one. Go.',
    },
    {
      zone: 'verify',
      label: 'Verify',
      ask: 'When was the Voting Rights Act signed and by whom?',
      ai: 'The Voting Rights Act was signed on August 6, 1965, by President Lyndon B. Johnson.',
      move: 'Sounds confident. Open one other source.',
    },
    {
      zone: 'walk-away',
      label: 'Walk away',
      ask: 'Should I quit the basketball team to focus on my grades?',
      ai: 'A thoughtful five-paragraph response weighing time, stress, identity, and college applications.',
      move: 'The AI does not know you. Close the chat.',
    },
  ];
  return (
    <div className="my-6 grid gap-4 lg:grid-cols-3">
      {scenarios.map((s) => (
        <ZoneScenarioCard key={s.zone} {...s} />
      ))}
    </div>
  );
}

function ZoneScenarioCard({
  zone,
  label,
  ask,
  ai,
  move,
}: {
  zone: 'trust' | 'verify' | 'walk-away';
  label: string;
  ask: string;
  ai: string;
  move: string;
}) {
  const styles = {
    trust: {
      border: 'border-emerald-500/30',
      bg: 'bg-emerald-500/[0.04]',
      badgeBg: 'bg-emerald-500/20',
      badgeText: 'text-emerald-300',
      moveText: 'text-emerald-300',
    },
    verify: {
      border: 'border-amber/40',
      bg: 'bg-amber/[0.04]',
      badgeBg: 'bg-amber/20',
      badgeText: 'text-amber',
      moveText: 'text-amber',
    },
    'walk-away': {
      border: 'border-rose-500/30',
      bg: 'bg-rose-500/[0.04]',
      badgeBg: 'bg-rose-500/20',
      badgeText: 'text-rose-300',
      moveText: 'text-rose-300',
    },
  }[zone];
  return (
    <div className={`rounded-xl border ${styles.border} ${styles.bg} p-5`}>
      <div className="flex flex-col gap-1.5">
        <span
          className={`inline-block w-fit shrink-0 rounded-md ${styles.badgeBg} px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${styles.badgeText}`}
        >
          {label}
        </span>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          What the student asked
        </p>
      </div>
      <p className="mt-2 rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text/90">
        {ask}
      </p>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-muted">
        What the AI gave back
      </p>
      <p className="mt-2 text-sm text-text/90">{ai}</p>
      <p
        className={`mt-4 font-mono text-[10px] uppercase tracking-widest ${styles.moveText}`}
      >
        Right next move: {move}
      </p>
    </div>
  );
}

function TrustLadder() {
  const rungs = [
    {
      zone: 'Trust',
      cond: 'Low stakes. Easily reversible. No facts under your name.',
      examples:
        'Brainstorms, tone fixes, vocab definitions, casual writing help, idea sparring.',
      move: 'Use it. Move on. Re-reading is wasted time.',
      accent: 'emerald',
    },
    {
      zone: 'Verify',
      cond: 'Going out under your name. Facts, numbers, dates, names, citations, or code that runs.',
      examples:
        'Essays, research, recommendations, anything a reader will believe because you wrote it.',
      move: 'Use it, but check the bones with one of the four methods first.',
      accent: 'amber',
    },
    {
      zone: 'Walk away',
      cond: 'Judgment, ethics, things only you can know, or three failed follow-ups.',
      examples:
        'Relationships, big personal decisions, your own beliefs, anything where you would not trust a stranger with the answer either.',
      move: 'Close the chat. Use a different tool: a person, a primary source, your own head.',
      accent: 'rose',
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          The trust ladder · Pick the zone before you act
        </p>
      </div>
      <div className="divide-y divide-line">
        {rungs.map((r) => {
          const accentColor =
            r.accent === 'emerald'
              ? 'text-emerald-300'
              : r.accent === 'amber'
              ? 'text-amber'
              : 'text-rose-300';
          const accentBg =
            r.accent === 'emerald'
              ? 'bg-emerald-500/20'
              : r.accent === 'amber'
              ? 'bg-amber/20'
              : 'bg-rose-500/20';
          return (
            <div
              key={r.zone}
              className="grid gap-3 px-5 py-5 md:grid-cols-[120px_1fr]"
            >
              <span
                className={`inline-block w-fit shrink-0 rounded-md ${accentBg} px-2 py-1 font-mono text-[11px] uppercase tracking-widest ${accentColor}`}
              >
                {r.zone}
              </span>
              <div className="space-y-2 text-sm text-text/90">
                <p>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                    When
                  </span>
                  <span className="ml-2">{r.cond}</span>
                </p>
                <p>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                    Examples
                  </span>
                  <span className="ml-2">{r.examples}</span>
                </p>
                <p>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                    Move
                  </span>
                  <span className={`ml-2 ${accentColor}`}>{r.move}</span>
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VerifyMethods() {
  const methods = [
    {
      n: '1',
      label: 'Cross-check',
      use: 'For facts, numbers, dates, names, citations.',
      how: 'Open one other source. Search the claim. Check the textbook. If the AI says Lincoln said it in 1858, find the speech.',
      example: '"What year was the Voting Rights Act signed?" Search the date in a separate tab.',
    },
    {
      n: '2',
      label: 'Reverse the prompt',
      use: 'For claims, conclusions, recommendations.',
      how: 'Ask the AI to argue the opposite of what it just said. If it cannot, the original was probably hedged correctly. If it can, you just learned what was missing.',
      example: '"You said X is the best choice. Now make the strongest case against X."',
    },
    {
      n: '3',
      label: 'Run it',
      use: 'For code, math, recipes, instructions, anything testable.',
      how: 'Execute it. Plug the numbers in. Try the recipe. Compile the code. Reality is a faster fact-checker than your eyes.',
      example: 'AI gives you a math answer. Open a calculator, do it yourself, see if they match.',
    },
    {
      n: '4',
      label: 'Ask a human',
      use: 'For judgment, ethics, tone, "is this weird?" calls.',
      how: 'Read the AI answer to someone you trust. Watch their face. Ask if it sounds right. Humans catch things AI cannot, especially anything about other humans.',
      example: '"Hey, does this email sound too pushy?" Read it to a parent or friend.',
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Four verification methods · Pick the one that fits the answer
        </p>
      </div>
      <div className="divide-y divide-line">
        {methods.map((m) => (
          <div
            key={m.n}
            className="grid gap-3 px-5 py-4 md:grid-cols-[40px_140px_1fr]"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber font-mono text-sm font-semibold text-ink">
              {m.n}
            </span>
            <div>
              <p className="font-mono text-sm uppercase tracking-widest text-amber">
                {m.label}
              </p>
              <p className="mt-1 text-xs text-muted">{m.use}</p>
            </div>
            <div className="space-y-1.5 text-sm text-text/90 md:pt-0.5">
              <p>{m.how}</p>
              <p className="text-xs text-muted">
                <span className="font-mono uppercase tracking-widest">Try</span>
                <span className="ml-2 italic">{m.example}</span>
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WalkAwaySignals() {
  const signals = [
    {
      n: '1',
      label: 'Only you can know',
      body: 'The question hinges on something inside you or your relationships: your feelings, your beliefs, what your friend can handle, what your gut is telling you. The model is generating a plausible-sounding answer to a question with no general right answer.',
    },
    {
      n: '2',
      label: 'High stakes plus no easy check',
      body: 'The decision has real consequences and cannot be quickly verified by a source, a person, or running it. Big personal choices, ethical calls, anything where being wrong matters and you would not trust a stranger with the answer either.',
    },
    {
      n: '3',
      label: 'Three failed follow-ups',
      body: 'You have given the model three rounds of feedback in the same chat and the answer is still off in the same direction. The model is not the bottleneck anymore. Your prompt was missing something fundamental, or the question is not in its lane. Either way, close the chat.',
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Three walk-away signals · Any one is enough
        </p>
      </div>
      <div className="divide-y divide-line">
        {signals.map((s) => (
          <div
            key={s.n}
            className="grid gap-3 px-5 py-4 md:grid-cols-[40px_180px_1fr]"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-rose-500/20 font-mono text-sm font-semibold text-rose-300">
              {s.n}
            </span>
            <p className="font-mono text-sm uppercase tracking-widest text-rose-300">
              {s.label}
            </p>
            <p className="text-sm text-text/90 md:pt-1">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ZoneDrill({
  n,
  scenario,
  output,
  correct,
  verdict,
}: {
  n: number;
  scenario: string;
  output: string;
  correct: 'trust' | 'verify' | 'walk-away';
  verdict: string;
}) {
  const [picked, setPicked] = useState<'trust' | 'verify' | 'walk-away' | null>(
    null
  );
  const isCorrect = picked === correct;
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Drill {n} · The scenario
        </p>
        <p className="mt-1.5 text-sm text-text/90">{scenario}</p>
      </div>
      <div className="px-5 py-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          What the AI gave you
        </p>
        <p className="mt-2 rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text/90">
          {output}
        </p>

        <p className="mt-5 font-mono text-[10px] uppercase tracking-widest text-muted">
          Pick the zone
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <ZoneButton
            zone="trust"
            label="Trust"
            sub="Use it. Move on."
            selected={picked === 'trust'}
            verdict={picked ? (correct === 'trust' ? 'right' : 'wrong') : null}
            onClick={() => setPicked('trust')}
          />
          <ZoneButton
            zone="verify"
            label="Verify"
            sub="Check the bones."
            selected={picked === 'verify'}
            verdict={picked ? (correct === 'verify' ? 'right' : 'wrong') : null}
            onClick={() => setPicked('verify')}
          />
          <ZoneButton
            zone="walk-away"
            label="Walk away"
            sub="Wrong tool. Different question."
            selected={picked === 'walk-away'}
            verdict={
              picked ? (correct === 'walk-away' ? 'right' : 'wrong') : null
            }
            onClick={() => setPicked('walk-away')}
          />
        </div>

        {picked && (
          <div
            className={`mt-5 rounded-md border px-4 py-3 ${
              isCorrect
                ? 'border-emerald-500/30 bg-emerald-500/[0.04]'
                : 'border-amber/40 bg-amber/[0.04]'
            }`}
          >
            <p
              className={`font-mono text-[10px] uppercase tracking-widest ${
                isCorrect ? 'text-emerald-300' : 'text-amber'
              }`}
            >
              {isCorrect ? 'Correct' : 'Not quite'} · Model verdict
            </p>
            <p className="mt-1.5 text-sm text-text/90">{verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ZoneButton({
  label,
  sub,
  selected,
  verdict,
  onClick,
}: {
  zone: 'trust' | 'verify' | 'walk-away';
  label: string;
  sub: string;
  selected: boolean;
  verdict: 'right' | 'wrong' | null;
  onClick: () => void;
}) {
  const borderClass =
    verdict === 'right' && selected
      ? 'border-emerald-500/50 bg-emerald-500/[0.05]'
      : verdict === 'wrong' && selected
      ? 'border-rose-500/50 bg-rose-500/[0.05]'
      : selected
      ? 'border-amber bg-amber/[0.06]'
      : 'border-line bg-ink/40 hover:border-amber/50';
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg border p-4 transition-colors ${borderClass}`}
    >
      <p
        className={`font-mono text-[11px] uppercase tracking-widest ${
          selected ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{sub}</p>
    </button>
  );
}

function ZoneTile({
  zone,
  label,
  q,
  sub,
}: {
  zone: 'trust' | 'verify' | 'walk-away';
  label: string;
  q: string;
  sub: string;
}) {
  const styles = {
    trust: {
      badgeBg: 'bg-emerald-500/20',
      badgeText: 'text-emerald-300',
    },
    verify: {
      badgeBg: 'bg-amber/20',
      badgeText: 'text-amber',
    },
    'walk-away': {
      badgeBg: 'bg-rose-500/20',
      badgeText: 'text-rose-300',
    },
  }[zone];
  return (
    <div className="rounded-lg border border-line bg-ink/60 p-5">
      <div className="flex items-center gap-3">
        <span
          className={`rounded-md ${styles.badgeBg} px-2 py-1 font-mono text-[11px] uppercase tracking-widest ${styles.badgeText}`}
        >
          {label}
        </span>
      </div>
      <p className="mt-3 text-base font-semibold text-text">{q}</p>
      <p className="mt-2 text-sm leading-relaxed text-text/90">{sub}</p>
    </div>
  );
}

// ---------- Next up ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 2.4
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            The AI as a teammate, not a replacement
          </h3>
          <p className="mt-2 text-sm text-muted">
            You can pick the tool, write the prompt, audit the answer, and
            decide what to do with it. The last piece is posture: how you
            actually work with AI day to day so it makes you sharper instead
            of softer. The character thread that ties Week 2 together.
          </p>
        </div>
        <button className="shrink-0 rounded-md border border-line px-4 py-2 text-sm text-muted hover:border-amber hover:text-text">
          Coming soon
        </button>
      </div>
    </div>
  );
}

// ---------- Shared primitives ----------

function Subhead({ n, title }: { n: string; title: string }) {
  return (
    <h3 className="mt-8 flex items-baseline gap-3 text-lg font-semibold text-text">
      <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {n}
      </span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  hook,
  matters,
}: {
  term: string;
  definition: string;
  analogy: string;
  hook: string;
  matters: string;
}) {
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Vocab · 2.3 · New term
        </p>
        <h4 className="mt-1 text-xl font-semibold text-text">{term}</h4>
      </div>
      <div className="space-y-4 px-5 py-5">
        {!revealed && (
          <>
            <p className="text-sm text-muted">
              Write your own one-sentence definition first. Then reveal.
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Your definition of "${term}"...`}
              className="h-20 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
            />
            <button
              onClick={() => setRevealed(true)}
              className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
            >
              Reveal definition →
            </button>
          </>
        )}
        {revealed && (
          <div className="space-y-3">
            <Block label="Definition" body={definition} />
            <Block label="Analogy" body={analogy} accent="amber" />
            <Block label="Memory hook" body={hook} />
            <Block label="Why it matters" body={matters} />
          </div>
        )}
      </div>
    </div>
  );
}

function Block({
  label,
  body,
  accent = 'plain',
}: {
  label: string;
  body: string;
  accent?: 'plain' | 'amber';
}) {
  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        accent === 'amber'
          ? 'border-amber/40 bg-amber/[0.04]'
          : 'border-line bg-ink/40'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          accent === 'amber' ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: '2.2 review' },
    { id: 'sec-teaching', label: 'Trust ladder' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
